import React, { useCallback, useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowClockwise,
    CheckCircleFill,
    EnvelopeCheckFill,
    ExclamationTriangleFill,
    ShieldLockFill,
    Tools,
} from 'react-bootstrap-icons';

import {
    getPlatformHealth,
    getPlatformSettings,
    updatePlatformSettings,
    type PlatformHealthResponse,
    type PlatformHealthService,
    type PlatformSettings,
    type ServiceMaintenanceSetting,
} from '../api/platformSettings';

import './SuperAdminPlans-new.css';
import './SuperAdminPlatformSettings-new.css';

type NoticeKind = 'success' | 'error';

interface Notice
{
    kind: NoticeKind;
    message: string;
}

type ToggleKey = 'company_email_only' | 'privileged_mfa_required';

const HIDDEN_SERVICE_MAINTENANCE_KEYS = new Set([
    'azure_devops',
]);

const DEFAULT_SETTINGS: PlatformSettings = {
    company_email_only: true,
    privileged_mfa_required: true,
    privileged_mfa_effective: true,
    mfa_environment_enabled: true,
    service_maintenance: [],
};

function serviceMessageDrafts(settings: PlatformSettings): Record<string, string>
{
    return Object.fromEntries(
        (settings.service_maintenance || []).map((service) => [
            service.service_key,
            service.message || '',
        ]),
    );
}

function formatCheckedAt(value?: string | null): string
{
    if (!value)
    {
        return 'Not checked yet';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
    {
        return 'Recently checked';
    }
    return parsed.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function formatHealthDetailKey(key: string): string
{
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatHealthDetailValue(value: unknown): string
{
    if (Array.isArray(value))
    {
        return value.join(', ');
    }
    if (value === null || value === undefined || value === '')
    {
        return '-';
    }
    return String(value);
}

function SuperAdminPlatformSettings(): React.ReactElement
{
    const [platformSettings, setPlatformSettings] = useState<PlatformSettings>(DEFAULT_SETTINGS);
    const [platformHealth, setPlatformHealth] = useState<PlatformHealthResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [healthLoading, setHealthLoading] = useState<boolean>(true);
    const [savingKey, setSavingKey] = useState<ToggleKey | null>(null);
    const [savingServiceKey, setSavingServiceKey] = useState<string | null>(null);
    const [messageDrafts, setMessageDrafts] = useState<Record<string, string>>({});
    const [notice, setNotice] = useState<Notice | null>(null);
    const [healthError, setHealthError] = useState<string | null>(null);

    const loadSettings = useCallback(async () =>
    {
        setLoading(true);
        setNotice(null);
        try
        {
            const settings = await getPlatformSettings();
            setPlatformSettings(settings);
            setMessageDrafts(serviceMessageDrafts(settings));
        }
        catch (err: any)
        {
            setNotice({
                kind: 'error',
                message: err?.response?.data?.detail || 'Failed to load platform settings',
            });
        }
        finally
        {
            setLoading(false);
        }
    }, []);

    const loadHealth = useCallback(async () =>
    {
        setHealthLoading(true);
        setHealthError(null);
        try
        {
            const health = await getPlatformHealth();
            setPlatformHealth(health);
        }
        catch (err: any)
        {
            setHealthError(err?.response?.data?.detail || err?.message || 'Failed to load platform health');
        }
        finally
        {
            setHealthLoading(false);
        }
    }, []);

    useEffect(() =>
    {
        void loadSettings();
        void loadHealth();
    }, [loadHealth, loadSettings]);

    async function handleToggle(key: ToggleKey): Promise<void>
    {
        if (loading || savingKey || savingServiceKey)
        {
            return;
        }

        const previous = platformSettings;
        const next = !previous[key];
        setPlatformSettings((current) => ({
            ...current,
            [key]: next,
            privileged_mfa_effective:
                key === 'privileged_mfa_required'
                    ? current.mfa_environment_enabled && next
                    : current.privileged_mfa_effective,
        }));
        setSavingKey(key);
        setNotice(null);

        try
        {
            const updated = await updatePlatformSettings({ [key]: next });
            setPlatformSettings(updated);
            setNotice({
                kind: 'success',
                message: getSaveMessage(key, Boolean(updated[key])),
            });
        }
        catch (err: any)
        {
            setPlatformSettings(previous);
            setNotice({
                kind: 'error',
                message: err?.response?.data?.detail || 'Failed to update platform settings',
            });
        }
        finally
        {
            setSavingKey(null);
        }
    }

    function getSaveMessage(key: ToggleKey, enabled: boolean): string
    {
        if (key === 'privileged_mfa_required')
        {
            return enabled
                ? 'Privileged MFA requirement enabled.'
                : 'Privileged MFA requirement disabled.';
        }
        return enabled
            ? 'Work email requirement enabled for early-access applications.'
            : 'Work email requirement disabled for early-access applications.';
    }

    function handleServiceMessageChange(serviceKey: string, value: string): void
    {
        setMessageDrafts((current) => ({
            ...current,
            [serviceKey]: value,
        }));
    }

    async function saveServiceMaintenance(
        service: ServiceMaintenanceSetting,
        nextEnabled?: boolean,
    ): Promise<void>
    {
        if (loading || savingKey || savingServiceKey)
        {
            return;
        }

        const previous = platformSettings;
        const maintenanceEnabled = nextEnabled ?? service.maintenance_enabled;
        const draftMessage = messageDrafts[service.service_key] ?? service.message ?? '';
        const effectiveMessage = draftMessage.trim() || service.effective_message;
        setSavingServiceKey(service.service_key);
        setNotice(null);
        setPlatformSettings((current) => ({
            ...current,
            service_maintenance: current.service_maintenance.map((item) =>
                item.service_key === service.service_key
                    ? {
                        ...item,
                        maintenance_enabled: maintenanceEnabled,
                        message: draftMessage.trim() || null,
                        effective_message: effectiveMessage,
                    }
                    : item,
            ),
        }));

        try
        {
            const updated = await updatePlatformSettings({
                service_maintenance: [{
                    service_key: service.service_key,
                    maintenance_enabled: maintenanceEnabled,
                    message: draftMessage,
                }],
            });
            setPlatformSettings(updated);
            setMessageDrafts(serviceMessageDrafts(updated));
            setNotice({
                kind: 'success',
                message: maintenanceEnabled
                    ? `${service.service_name} maintenance enabled.`
                    : `${service.service_name} is available.`,
            });
        }
        catch (err: any)
        {
            setPlatformSettings(previous);
            setMessageDrafts(serviceMessageDrafts(previous));
            setNotice({
                kind: 'error',
                message: err?.response?.data?.detail || 'Failed to update service availability',
            });
        }
        finally
        {
            setSavingServiceKey(null);
        }
    }

    const companyEmailOnly = platformSettings.company_email_only;
    const privilegedMfaRequired = platformSettings.privileged_mfa_required;
    const mfaEnvironmentEnabled = platformSettings.mfa_environment_enabled;
    const mfaDisabledByEnvironment = !mfaEnvironmentEnabled;
    const companyStatusLabel = loading ? 'Loading...' : companyEmailOnly ? 'Enabled' : 'Disabled';
    const mfaStatusLabel = loading
        ? 'Loading...'
        : mfaDisabledByEnvironment
            ? 'Disabled by env'
            : privilegedMfaRequired
                ? 'Enabled'
                : 'Disabled';
    const serviceMaintenance = (platformSettings.service_maintenance || []).filter(
        (service) => !HIDDEN_SERVICE_MAINTENANCE_KEYS.has(service.service_key),
    );
    const healthServices = platformHealth?.services || [];
    const unhealthyServiceCount = healthServices.filter((service) => service.status !== 'healthy').length;
    const healthStatusLabel = healthLoading && !platformHealth
        ? 'Checking'
        : platformHealth?.status === 'healthy'
            ? 'Healthy'
            : platformHealth
                ? 'Needs attention'
                : 'Unavailable';
    const busy = loading || savingKey !== null || savingServiceKey !== null;
    const refreshBusy = busy || healthLoading;

    return (
        <div className="sa-plans-page platform-settings-page">
            <div className="sa-plans-breadcrumbs">Super Admin - Platform</div>
            <div className="sa-plans-header-row">
                <div>
                    <h1 className="sa-plans-title">Platform Configurations</h1>
                    <p className="sa-plans-subtitle">
                        Manage global controls used across AcceliQA.
                    </p>
                </div>
                <div className="sa-plans-actions">
                    <button
                        type="button"
                        className="sa-plans-btn sa-plans-btn-icon"
                        onClick={() =>
                        {
                            void loadSettings();
                            void loadHealth();
                        }}
                        disabled={refreshBusy}
                    >
                        {refreshBusy ? <Spinner animation="border" size="sm" /> : <ArrowClockwise size={14} />}
                        Refresh
                    </button>
                </div>
            </div>

            {notice && (
                <div
                    className={notice.kind === 'success' ? 'sa-plans-success' : 'sa-plans-error'}
                    role={notice.kind === 'success' ? 'status' : 'alert'}
                >
                    {notice.message}
                </div>
            )}

            <div className="platform-config-layout">
                <div className="platform-config-dashboard-column">
            <section className="sa-plans-card platform-settings-card platform-health-card" aria-label="System health">
                <div className="platform-settings-card-header">
                    <div>
                        <h2 className="platform-settings-card-title">System Health</h2>
                        <p className="platform-settings-card-subtitle">
                            Live dependency checks used by server readiness probes.
                        </p>
                    </div>
                    <span className={`platform-health-overall ${platformHealth?.status === 'healthy' ? 'is-healthy' : 'is-unhealthy'}`}>
                        {healthLoading && <Spinner animation="border" size="sm" />}
                        {healthStatusLabel}
                    </span>
                </div>

                <div className="platform-health-summary">
                    <div className="platform-health-summary-item">
                        <span className="platform-health-summary-label">Last checked</span>
                        <span className="platform-health-summary-value">{formatCheckedAt(platformHealth?.checked_at)}</span>
                    </div>
                    <div className="platform-health-summary-item">
                        <span className="platform-health-summary-label">Services checked</span>
                        <span className="platform-health-summary-value">{healthServices.length || '-'}</span>
                    </div>
                    <div className="platform-health-summary-item">
                        <span className="platform-health-summary-label">Attention needed</span>
                        <span className="platform-health-summary-value">{unhealthyServiceCount}</span>
                    </div>
                </div>

                {healthError && (
                    <div className="platform-health-error" role="alert">
                        {healthError}
                    </div>
                )}

                <div className="platform-health-grid">
                    {healthLoading && healthServices.length === 0 && (
                        <div className="platform-health-loading">
                            <Spinner animation="border" size="sm" />
                            Checking services...
                        </div>
                    )}
                    {!healthLoading && !healthError && healthServices.length === 0 && (
                        <div className="platform-health-loading">No health results available.</div>
                    )}
                    {healthServices.map((service: PlatformHealthService) =>
                    {
                        const healthy = service.status === 'healthy';
                        const detailPairs = Object.entries(service.details || {}).slice(0, 3);
                        return (
                            <div
                                key={service.key}
                                className={`platform-health-tile ${healthy ? 'is-healthy' : 'is-unhealthy'}`}
                            >
                                <div className="platform-health-tile-header">
                                    <span className="platform-health-icon" aria-hidden="true">
                                        {healthy ? <CheckCircleFill size={16} /> : <ExclamationTriangleFill size={16} />}
                                    </span>
                                    <span className="platform-health-service-name">{service.label}</span>
                                    <span className={`platform-health-status ${healthy ? 'is-healthy' : 'is-unhealthy'}`}>
                                        {healthy ? 'Healthy' : 'Issue'}
                                    </span>
                                </div>
                                <div className="platform-health-meta">
                                    <span>{service.latency_ms != null ? `${service.latency_ms} ms` : 'Latency -'}</span>
                                </div>
                                {service.error ? (
                                    <p className="platform-health-error-text">{service.error}</p>
                                ) : detailPairs.length > 0 ? (
                                    <dl className="platform-health-details">
                                        {detailPairs.map(([key, value]) => (
                                            <React.Fragment key={key}>
                                                <dt>{formatHealthDetailKey(key)}</dt>
                                                <dd>{formatHealthDetailValue(value)}</dd>
                                            </React.Fragment>
                                        ))}
                                    </dl>
                                ) : (
                                    <p className="platform-health-muted">No additional detail.</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
                </div>

                <div className="platform-config-controls-column">
            <section className="sa-plans-card platform-settings-card" aria-label="Platform settings">
                <div className="platform-settings-card-header">
                    <div>
                        <h2 className="platform-settings-card-title">Access Controls</h2>
                        <p className="platform-settings-card-subtitle">
                            Global sign-up and access rules.
                        </p>
                    </div>
                    <span className="platform-settings-count">2 settings</span>
                </div>

                <div className="platform-setting-row">
                    <span className="platform-setting-icon" aria-hidden="true">
                        <EnvelopeCheckFill size={18} />
                    </span>
                    <span className="platform-setting-copy">
                        <span className="platform-setting-title">Work email only</span>
                        <span className="platform-setting-description">
                            Early-access requests must use a company or work email address.
                        </span>
                    </span>
                    <span className={`platform-setting-status ${companyEmailOnly ? 'is-enabled' : 'is-disabled'}`}>
                        {companyStatusLabel}
                    </span>
                    <button
                        type="button"
                        className={`platform-setting-toggle ${companyEmailOnly ? 'platform-setting-toggle-on' : ''}`}
                        role="switch"
                        aria-checked={companyEmailOnly}
                        aria-label="Work email only"
                        onClick={() => void handleToggle('company_email_only')}
                        disabled={busy}
                    >
                        <span className="platform-setting-toggle-knob" />
                    </button>
                </div>

                <div className={`platform-setting-row ${mfaDisabledByEnvironment ? 'platform-setting-row-muted' : ''}`}>
                    <span className="platform-setting-icon platform-setting-icon-security" aria-hidden="true">
                        <ShieldLockFill size={18} />
                    </span>
                    <span className="platform-setting-copy">
                        <span className="platform-setting-title">Privileged MFA</span>
                        <span className="platform-setting-description">
                            Super admins and organization admins must verify sign-in with email OTP.
                        </span>
                    </span>
                    <span className={`platform-setting-status ${platformSettings.privileged_mfa_effective ? 'is-enabled' : 'is-disabled'}`}>
                        {mfaStatusLabel}
                    </span>
                    <button
                        type="button"
                        className={`platform-setting-toggle ${privilegedMfaRequired && mfaEnvironmentEnabled ? 'platform-setting-toggle-on' : ''}`}
                        role="switch"
                        aria-checked={privilegedMfaRequired && mfaEnvironmentEnabled}
                        aria-label="Privileged MFA"
                        onClick={() => void handleToggle('privileged_mfa_required')}
                        disabled={busy || mfaDisabledByEnvironment}
                    >
                        <span className="platform-setting-toggle-knob" />
                    </button>
                </div>
            </section>

            <section className="sa-plans-card platform-settings-card platform-service-card" aria-label="Service availability">
                <div className="platform-settings-card-header">
                    <div>
                        <h2 className="platform-settings-card-title">Service Availability</h2>
                        <p className="platform-settings-card-subtitle">
                            Pause new service work during incidents or maintenance windows.
                        </p>
                    </div>
                    <span className="platform-settings-count">{serviceMaintenance.length} services</span>
                </div>

                {serviceMaintenance.map((service) =>
                {
                    const draft = messageDrafts[service.service_key] ?? service.message ?? '';
                    const savedMessage = service.message || '';
                    const messageChanged = draft.trim() !== savedMessage.trim();
                    const serviceBusy = savingServiceKey === service.service_key;
                    return (
                        <div
                            key={service.service_key}
                            className={`platform-service-row ${service.maintenance_enabled ? 'is-maintenance' : ''}`}
                        >
                            <span className="platform-setting-icon platform-setting-icon-service" aria-hidden="true">
                                {serviceBusy ? <Spinner animation="border" size="sm" /> : <Tools size={18} />}
                            </span>
                            <span className="platform-setting-copy">
                                <span className="platform-setting-title">{service.service_name}</span>
                                <span className="platform-setting-description">{service.description}</span>
                            </span>
                            <span className={`platform-setting-status ${service.maintenance_enabled ? 'is-maintenance' : 'is-enabled'}`}>
                                {service.maintenance_enabled ? 'Maintenance' : 'Available'}
                            </span>
                            <button
                                type="button"
                                className={`platform-setting-toggle ${!service.maintenance_enabled ? 'platform-setting-toggle-on' : ''}`}
                                role="switch"
                                aria-checked={!service.maintenance_enabled}
                                aria-label={`${service.service_name} availability`}
                                onClick={() => void saveServiceMaintenance(service, !service.maintenance_enabled)}
                                disabled={busy}
                            >
                                <span className="platform-setting-toggle-knob" />
                            </button>

                            {service.maintenance_enabled && (
                                <div className="platform-service-message">
                                    <label htmlFor={`service-message-${service.service_key}`}>
                                        Maintenance message
                                    </label>
                                    <textarea
                                        id={`service-message-${service.service_key}`}
                                        value={draft}
                                        placeholder={service.effective_message}
                                        onChange={(event) => handleServiceMessageChange(service.service_key, event.target.value)}
                                        disabled={busy}
                                        rows={2}
                                    />
                                    <button
                                        type="button"
                                        className="sa-plans-btn sa-plans-btn-secondary platform-service-save"
                                        onClick={() => void saveServiceMaintenance(service)}
                                        disabled={busy || !messageChanged}
                                    >
                                        Save message
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </section>
                </div>
            </div>
        </div>
    );
}

export default SuperAdminPlatformSettings;
